import * as R from 'remeda';
import { GridFilterItem, GridLogicOperator } from '@rossum/ui/x-data-grid-pro';
import { absoluteApiUrl } from '../../constants/config';
import { CodecFieldType } from './constants';

export const createNewFilterQuery = (newFilterItems: GridFilterItem[]) =>
  JSON.stringify({
    items: newFilterItems,
    logicOperator: GridLogicOperator.And,
  });

type ID = string | number;

// Reference fields whose values are ids on the grid but resource URLs in the
// query — mapped to the API resource path used to build/parse those URLs.
const referenceFieldPaths = {
  labels: '/labels',
  annotation: '/annotations',
  queue: '/queues',
  assignees: '/users',
  modifier: '/users',
  exported_by: '/users',
  confirmed_by: '/users',
  rejected_by: '/users',
  deleted_by: '/users',
  creator: '/users',
};

const isReferenceField = (
  field: string
): field is keyof typeof referenceFieldPaths => field in referenceFieldPaths;

/**
 * Infer the field data type from field name suffix convention (e.g. `field.vendor_id.number`).
 * Returns undefined if the suffix doesn't match a known type.
 */
export const inferFieldType = (field: string): CodecFieldType | undefined => {
  const suffix = field.split('.').pop();
  if (suffix === 'number') return 'number';
  if (suffix === 'date') return 'date';
  if (suffix === 'string') return 'string';
  return undefined;
};

const isValidNumber = (value: unknown) =>
  R.isNumber(value) && !Number.isNaN(value);

/* -------------------------------------------------------------------------- */
/* Concern: id <-> resource URL                                               */
/* -------------------------------------------------------------------------- */

// A single id or an array of ids (the shape that can be turned into URL(s)).
const isIdOrIdArray = (value: unknown): value is ID | ID[] => {
  if (R.isString(value) || isValidNumber(value)) return true;

  if (R.isArray(value))
    return value.every(v => R.isString(v) || isValidNumber(v));

  return false;
};

/**
 * Grid stores reference fields (queue / assignees / labels / modifiers) as raw
 * ids, but the backend query addresses them by resource URL. Non-reference
 * fields, and non-id values (e.g. the boolean of isEmpty / isNotEmpty), pass
 * through untouched.
 */
const transformIdsToUrls = (value: unknown, field: string) => {
  const fieldPath = isReferenceField(field) ? referenceFieldPaths[field] : null;

  const toUrl = (id: ID, path: string) => {
    if (typeof id === 'string' && id.startsWith(absoluteApiUrl)) {
      return id;
    }

    return `${absoluteApiUrl}${path}/${id}`;
  };

  return fieldPath && isIdOrIdArray(value)
    ? R.isArray(value)
      ? value.map(id => toUrl(id, fieldPath))
      : toUrl(value, fieldPath)
    : value;
};

/**
 * Inverse of `transformIdsToUrls`: reduces a resource URL back to its trailing
 * id (the raw id the FE filter selects match on). Only reference fields are
 * reversed; a non-URL value on a reference field, or any value on another
 * field, is left as-is.
 */
const transformUrlsToIds = (value: unknown, field: string) => {
  if (!isReferenceField(field)) return value;

  const toId = (v: unknown) =>
    R.isString(v) && v.startsWith(absoluteApiUrl)
      ? (v.split('/').pop() ?? v)
      : v;

  return R.isArray(value) ? value.map(toId) : toId(value);
};

/* -------------------------------------------------------------------------- */
/* Concern: numeric coercion (one-way — a decoded number is already a number) */
/* -------------------------------------------------------------------------- */

const coerceNumericValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(coerceNumericValue);
  }

  if (typeof value === 'string' && value !== '') {
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  }

  return value;
};

// Inverse of `coerceNumericValue`: the grid stores number-filter values as
// strings (the text field is a string input), but a stored MQL query holds them
// as real numbers. Turn them back into strings (recursing into the array carried
// by $in/$nin) so the chip label and filter component render the value.
const stringifyNumericValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stringifyNumericValue);
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }

  return value;
};

/* -------------------------------------------------------------------------- */
/* Concern: boolean coercion                                                  */
/* -------------------------------------------------------------------------- */

// Boolean columns are filtered via a Yes/No select whose option values are the
// strings 'true'/'false'; map them back to real booleans (recursing into the
// array carried by $in/$nin) so the backend compares against stored booleans.
const coerceBooleanValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(coerceBooleanValue);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
};

// Inverse of `coerceBooleanValue`: real booleans back to the Yes/No select's
// 'true'/'false' strings so the decoded value matches an option.
const reverseBooleanValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(reverseBooleanValue);
  if (value === true) return 'true';
  if (value === false) return 'false';
  return value;
};

/* -------------------------------------------------------------------------- */
/* Concern: trimming (one-way — whitespace is never restored)                 */
/* -------------------------------------------------------------------------- */

const trimStringValues = (value: unknown) => {
  if (R.isString(value)) return value.trim();

  if (R.isArray(value)) return value.map(v => (R.isString(v) ? v.trim() : v));

  return value;
};

/* -------------------------------------------------------------------------- */
/* Shared codec pipeline                                                      */
/* -------------------------------------------------------------------------- */

type ValueContext = { field: string; codecFieldType?: string };
type ValueTransform = (value: unknown, ctx: ValueContext) => unknown;
type ValueCodec = { encode: ValueTransform; decode: ValueTransform };

/**
 * Every value concern shared by encoding (grid → MQL) and decoding (MQL → grid)
 * lives here as a codec declaring BOTH directions. `satisfies Record<string,
 * ValueCodec>` makes the pairing mandatory: a new concern cannot be added with
 * only an `encode` — its `decode` must be declared too. Use `identity` when a
 * concern is a one-way sanitization/coercion with no meaningful inverse; that
 * is a conscious statement, not a forgotten reverse.
 *
 */
const valueCodecs = {
  // grid selects match raw ids; the backend query expects resource URLs
  idUrl: {
    encode: (value, { field }) => transformIdsToUrls(value, field),
    decode: (value, { field }) => transformUrlsToIds(value, field),
  },
  // user-entered whitespace is stripped on the way out; nothing to restore
  trim: {
    encode: value => trimStringValues(value),
    decode: R.identity(),
  },
  // number columns: text value → number for the query; number → text on read
  // (the grid renders number-filter values from string state)
  numeric: {
    encode: (value, { codecFieldType }) =>
      codecFieldType === 'number' ? coerceNumericValue(value) : value,
    decode: (value, { codecFieldType }) =>
      codecFieldType === 'number' ? stringifyNumericValue(value) : value,
  },
  // bool columns: Yes/No select's 'true'/'false' strings ↔ real booleans
  boolean: {
    encode: (value, { codecFieldType }) =>
      codecFieldType === 'bool' ? coerceBooleanValue(value) : value,
    decode: (value, { codecFieldType }) =>
      codecFieldType === 'bool' ? reverseBooleanValue(value) : value,
  },
} satisfies Record<string, ValueCodec>;

const codecPipeline: ValueCodec[] = R.values(valueCodecs);

/**
 * Prepare a grid filter value for an MQL query by applying every shared concern
 * (id→URL, trim, numeric/boolean coercion) in order.
 */
export const encodeValue = (value: unknown, ctx: ValueContext): unknown =>
  codecPipeline.reduce((acc, codec) => codec.encode(acc, ctx), value);

/**
 * Restore a stored MQL value to the grid filter shape by applying each codec's
 * `decode`. Every concern today operates on a disjoint set of fields/types, so
 * the order matches `encodeValue`; if an order-sensitive concern is ever added,
 * decode must apply the codecs in reverse.
 */
export const decodeValue = (value: unknown, ctx: ValueContext): unknown =>
  codecPipeline.reduce((acc, codec) => codec.decode(acc, ctx), value);
